// Caller-authority derivation from memberships and organization ownership.
//
// The `/v1/me` contract forbids a `role`/`team` claim (docs/04 §11 `me`
// bullet — "no active-team claim, and no role claim"), so the
// console never reads a phantom `MeResponse.role`/`MeResponse.team` field.
// Instead a caller's authority is derived from their OWN active memberships
// (the `roles_for_user` semantics, reused client-side over human reads) PLUS
// the org-owner check. These drive AFFORDANCES only — every `/v1` route stays
// server-authoritative (docs/05 §6.4).

import { useQuery } from "@tanstack/react-query";

import type { components } from "../api/schema";
import { apiClient } from "./apiClient";
import { ApiRequestError } from "./errors";
import { MAX_PAGE_SIZE } from "./pagination";
import { queryKeys } from "./queryKeys";

type MemberRole = components["schemas"]["MemberRole"];
type MeResponse = components["schemas"]["MeResponse"];
type Membership = components["schemas"]["MembershipResponse"];
type Organization = components["schemas"]["OrganizationResponse"];
type AlertChannelType = components["schemas"]["AlertChannelType"];

// docs/05 §6.4 role rank: viewer < member < admin < owner.
const ROLE_RANK: Record<MemberRole, number> = {
  viewer: 0,
  member: 1,
  admin: 2,
  owner: 3,
};

// Registry order for role `<Select>` options (owner first).
const MEMBER_ROLES: readonly MemberRole[] = [
  "owner",
  "admin",
  "member",
  "viewer",
];

// Org ownership uses `organizations.owner_app_user_id` (docs/05 §6.2), never a
// phantom `/v1/me` role claim.
export function isOrganizationOwner(
  me: MeResponse | undefined,
  organization: Organization | undefined,
): boolean {
  return (
    organization?.owner_app_user_id != null &&
    me?.user.id === organization.owner_app_user_id
  );
}

// The ceiling-capped roles a caller at `authority` may grant (docs/05 §6.4):
// an admin offers viewer|member|admin (NO owner); an owner offers all four.
export function grantableRoles(
  authority: MemberRole | undefined,
): MemberRole[] {
  const rank = authority === undefined ? -1 : ROLE_RANK[authority];
  return MEMBER_ROLES.filter((role) => ROLE_RANK[role] <= rank);
}

function maxRole(roles: readonly MemberRole[]): MemberRole | undefined {
  return roles.reduce<MemberRole | undefined>(
    (best, role) =>
      best === undefined || ROLE_RANK[role] > ROLE_RANK[best] ? role : best,
    undefined,
  );
}

export interface CallerAuthority {
  // Alert channel types authorable in the active install profile.
  alertChannelTypes: AlertChannelType[];
  // The caller's app_user id (`me.user.id`), once `/v1/me` resolves.
  appUserId?: string;
  // The caller's own active memberships across the org — every team, all pages.
  callerMemberships: Membership[];
  // Affordance gate: org owner OR authority ∈ {owner, admin} (docs/05 §6.4).
  canManage: boolean;
  errorMessage?: string;
  // Roles this caller may grant, ceiling-capped (docs/05 §6.4).
  grantableRoles: MemberRole[];
  isError: boolean;
  isOrgOwner: boolean;
  isPending: boolean;
  organizationId?: string;
  // Retry the authority reads after a cold-load failure.
  retry: () => void;
  // The caller's active role within a specific team, if any (owner-tier gates).
  roleForTeam: (teamId: string) => MemberRole | undefined;
}

export interface CallerAuthorityOptions {
  enabled?: boolean;
}

// Derive the caller's authority from the membership-derivation seam. Authority
// weighs ALL of the caller's memberships (not just one team-filtered/paginated
// page), so the keyset is paged to exhaustion here.
export function useCallerAuthority({
  enabled = true,
}: CallerAuthorityOptions = {}): CallerAuthority {
  const me = useQuery({
    enabled,
    queryKey: queryKeys.me,
    queryFn: async () => {
      const { data, error } = await apiClient.GET("/v1/me");
      if (error) {
        throw new ApiRequestError(error);
      }
      return data;
    },
  });

  const appUserId = me.data?.user.id;
  const organizationId = me.data?.organization.id;

  const organization = useQuery({
    enabled: enabled && Boolean(organizationId),
    queryKey: queryKeys.organizations.detail(organizationId ?? "pending"),
    queryFn: async () => {
      if (!organizationId) {
        throw new Error("Organization id is required.");
      }
      const { data, error } = await apiClient.GET("/v1/organizations/{id}", {
        params: { path: { id: organizationId } },
      });
      if (error) {
        throw new ApiRequestError(error);
      }
      return data;
    },
  });

  const memberships = useQuery({
    enabled: enabled && Boolean(appUserId),
    queryKey: queryKeys.memberships.list({ surface: "caller-authority" }),
    queryFn: async () => {
      const collected: Membership[] = [];
      let pageToken: string | undefined;
      do {
        const { data, error } = await apiClient.GET("/v1/memberships", {
          params: {
            query: { page_size: MAX_PAGE_SIZE, page_token: pageToken },
          },
        });
        if (error) {
          throw new ApiRequestError(error);
        }
        collected.push(...data.items);
        pageToken = data.next_page_token ?? undefined;
      } while (pageToken);
      return collected;
    },
  });

  const isOrgOwner = isOrganizationOwner(me.data, organization.data);
  const callerMemberships = (memberships.data ?? []).filter(
    (membership) =>
      membership.app_user_id === appUserId && membership.deleted_at == null,
  );
  const membershipAuthority = maxRole(
    callerMemberships.map((membership) => membership.member_role),
  );
  const authority: MemberRole | undefined = isOrgOwner
    ? "owner"
    : membershipAuthority;
  const canManage =
    isOrgOwner || authority === "owner" || authority === "admin";

  const roleForTeam = (teamId: string): MemberRole | undefined =>
    callerMemberships.find((membership) => membership.team_id === teamId)
      ?.member_role;

  const isPending =
    enabled &&
    (me.isPending ||
      (Boolean(organizationId) && organization.isPending) ||
      (Boolean(appUserId) && memberships.isPending));
  const isError =
    enabled && (me.isError || organization.isError || memberships.isError);
  const errorMessage = enabled
    ? me.isError
      ? me.error.message
      : organization.isError
        ? organization.error.message
        : memberships.isError
          ? memberships.error.message
          : undefined
    : undefined;

  const retry = () => {
    void me.refetch();
    if (organizationId) {
      void organization.refetch();
    }
    if (appUserId) {
      void memberships.refetch();
    }
  };

  return {
    alertChannelTypes:
      me.data?.alert_channel_capabilities.map(
        (capability) => capability.channel_type,
      ) ?? [],
    appUserId,
    callerMemberships,
    canManage,
    errorMessage,
    grantableRoles: grantableRoles(authority),
    isError,
    isOrgOwner,
    isPending,
    organizationId,
    retry,
    roleForTeam,
  };
}
